import React, { FC, useContext, useState } from "react";

import ProductModal from "~/modules/order/containers/ProductModal/ProductModal";
import Modal from "~/components/Modal/Modal";
import { CartLine, ResponseOrderWithCartLines } from "~/entities/orderEntities";
import OrderContext from "~/contexts/OrderContext";
import RemoveIcon from "~/icons/RemoveIcon";
import CartLineView from "../CartItem/CartItem";
import EmptyCartIcon from "~/icons/EmptyCartIcon";
import ThemeContext from "~/contexts/ThemeContext";
import TaxContext from "~/contexts/TaxContext";
import LoyaltyEarnings from "~/modules/loyalty/components/LoyaltyEarnings/LoyaltyEarnings";
import AuthContext from "~/contexts/AuthContext";

import styles from "./Cart.module.css";
import { MinimumCard } from "~/modules/catering/components/MinimumOrder/MinimumOrder";
import PromoCode from "~/modules/loyalty/components/PromoCode/PromoCode";
import UnlockedDeals from "~/modules/deals/components/UnlockedDeals/UnlockedDeals";
import AdditionalProducts from "../AdditionalProducts/AdditionalProducts";
import { getCartItemCount, getTotalPrice } from "~/helpers/cartHelpers";
import {
  CartItemNameFontSize,
  CartItemPriceFontSize,
} from "~/theme/models/themeModels";
import clsx from "clsx";
import { PaymentSurcharge } from "~/entities/paymentEntities";
import { Currency } from "~/entities/currencyEntities";
import {
  groupCartLines,
  DealGroup as DealGroupData,
} from "~/helpers/bundleDisplay";
import { offerRows } from "~/helpers/offerAmounts";
import { offerSavingColour } from "~/helpers/offerTheme";
import DealGroup from "../DealGroup/DealGroup";
import DealBuilderContext from "~/contexts/DealBuilderContext";
import DealNotice from "~/modules/deals/components/DealNotice/DealNotice";
import DealNudge from "~/modules/deals/components/DealNudge/DealNudge";
import RemoveDealItemSheet from "~/modules/deals/components/RemoveDealItemSheet/RemoveDealItemSheet";
import { withIndefiniteArticle } from "~/helpers/dealHelpers";

type CartProps = {
  title?: string;
  showAdditionalProducts: boolean;
  readOnly: boolean;
  clearable: boolean;
  hideAmountsForMobile: boolean;
  surchargeInfo?: PaymentSurcharge;
};

export type AmountRow = {
  className: string;
  label: string;
  amount: string | null | undefined;
  style?: React.CSSProperties;
};

// Totals: items at full price, one signed row per offer with what it saved, charges, the total, and what was saved
export const getAmounts = (
  orderSummary: ResponseOrderWithCartLines,
  formatAmount: (amount: number | string) => string | null | undefined,
  currency: Currency | null,
  surchargeInfo: PaymentSurcharge | undefined | null,
  classes: {
    discountClass: string;
    chargeClass: string;
    totalDiscountClass: string;
    totalClass: string;
  },
  savingColour?: string,
) => {
  const amounts: AmountRow[] = [];
  const savingStyle = savingColour ? { color: savingColour } : undefined;
  const offers = offerRows(orderSummary);

  if (offers.length) {
    amounts.push({
      className: classes.chargeClass,
      label: "Items",
      amount: formatAmount(orderSummary.lineTotalBeforeDiscount),
    });
  }

  for (const offer of offers) {
    amounts.push({
      className: classes.discountClass,
      label: offer.label,
      amount:
        offer.amount == null ? "…" : `\u2212${formatAmount(offer.amount)}`,
      style: savingStyle,
    });
  }

  orderSummary.additionalCharges.forEach((charge) => {
    amounts.push({
      className: classes.chargeClass,
      label: charge.name || `Additional charge`,
      amount: formatAmount(charge.amount),
    });
  });

  // Surcharge
  if (surchargeInfo) {
    surchargeInfo.details.forEach((details) => {
      amounts.push({
        className: classes.chargeClass,
        label: details.name || `Payment fee`,
        amount: formatAmount(details.amount),
      });
    });
  }

  // Total
  amounts.push({
    className: classes.totalClass,
    label: `Total ${
      currency?.stripeCurrency && `(${currency?.stripeCurrency.toUpperCase()})`
    }`.trim(),
    amount: formatAmount(
      (orderSummary.finalPriceIncTax ?? 0) +
        (surchargeInfo?.paymentFeeAmount ?? 0),
    ),
  });

  const saved =
    orderSummary.totalBeforeDiscount - orderSummary.finalPriceIncTax;
  if (saved > 0) {
    amounts.push({
      className: classes.totalDiscountClass,
      label: "You saved",
      amount: formatAmount(saved),
      style: savingStyle,
    });
  }

  return amounts;
};

const Cart: FC<CartProps> = ({
  title,
  showAdditionalProducts,
  readOnly,
  clearable,
  hideAmountsForMobile,
  surchargeInfo,
}) => {
  const [removeOrderItemId, setRemoveOrderItemId] = useState<string | null>(
    null,
  );
  const [removeDeal, setRemoveDeal] = useState<{
    name: string;
    lineIds: string[];
  } | null>(null);
  const [removeDealItem, setRemoveDealItem] = useState<{
    deal: DealGroupData;
    line: CartLine;
  } | null>(null);
  const [selectedLine, setSelectedLine] = useState<CartLine | null>(null);
  const [visibleResetCartModal, setVisibleResetCartModal] = useState(false);

  const { template } = useContext(ThemeContext);
  const { formatAmount, currency } = useContext(TaxContext);

  const {
    orderSummary,
    updateOrderLine,
    removeOrderLine,
    removeOrderLines,
    reset,
    orderSource,
    orderLoading,
  } = useContext(OrderContext);
  const entries = groupCartLines(orderSummary.lines);
  const { deals, openDealEditor, partialDeal } = useContext(DealBuilderContext);
  // Lines the "complete your deal" nudge is counting get a hint
  const dealHint = partialDeal
    ? `${partialDeal.deal.name}: add ${withIndefiniteArticle(
        partialDeal.slot.label.toLowerCase(),
      )}`
    : undefined;
  const { useComoAuth, authenticatedUser } = useContext(AuthContext);

  // Styles
  const cartBackgroundColor = template.cart.backgroundColour;
  const cartFontColor = template.cart.fontColour;

  const cartItemNameFontSize =
    CartItemNameFontSize[template.cart.item.nameFontSize];
  const cartItemNameFontColor = template.cart.item.nameFontColour;
  const cartItemPriceFontSize =
    CartItemPriceFontSize[template.cart.item.priceFontSize];
  const cartItemPriceFontColor = template.cart.item.priceFontColour;

  return (
    <div
      className={styles.cart}
      style={{
        backgroundColor: cartBackgroundColor,
        color: cartFontColor,
      }}
    >
      <ProductModal
        isInsert={false}
        initLine={selectedLine}
        onClose={() => setSelectedLine(null)}
      />
      <Modal
        title="Are you sure you want to remove this item from your cart?"
        visible={!!removeOrderItemId}
        onClose={() => setRemoveOrderItemId(null)}
        onConfirm={() => {
          if (removeOrderItemId) {
            removeOrderLine(removeOrderItemId);
            setRemoveOrderItemId(null);
          }
        }}
      />
      <Modal
        title={`Remove the whole ${removeDeal?.name ?? "deal"}?`}
        bodyText="All of its items will be removed from your cart."
        okText="Remove"
        visible={!!removeDeal}
        onClose={() => setRemoveDeal(null)}
        onConfirm={() => {
          if (removeDeal) {
            removeOrderLines(removeDeal.lineIds);
            setRemoveDeal(null);
          }
        }}
      />
      <RemoveDealItemSheet
        target={removeDealItem}
        onClose={() => setRemoveDealItem(null)}
      />
      <Modal
        title="Are you sure you want to empty your cart?"
        visible={visibleResetCartModal}
        onClose={() => setVisibleResetCartModal(false)}
        onConfirm={reset}
      />
      {orderSummary.lines.length ? (
        <>
          <div className={styles.scroll}>
            {showAdditionalProducts && <AdditionalProducts />}
            <div className={styles.title}>
              <span>
                {title || `My Cart (${getCartItemCount(orderSummary.lines)})`}
              </span>
              {!!orderSummary.lines.length && clearable && (
                <div
                  className={styles.clearButton}
                  onClick={() => setVisibleResetCartModal(true)}
                >
                  <RemoveIcon />
                  <span>Clear cart</span>
                </div>
              )}
            </div>
            {readOnly ? (
              <div className={styles.subTotals}>
                {entries.map((entry) =>
                  entry.type === "deal" ? (
                    <DealGroup
                      key={entry.deal.instanceId}
                      deal={entry.deal}
                      readOnly
                    />
                  ) : (
                    <div key={entry.line.id} className={styles.subTotalItem}>
                      <div className={styles.subTotalRow}>
                        <span
                          className={styles.subTotalName}
                          style={{
                            fontSize: cartItemNameFontSize,
                            color: cartItemNameFontColor,
                          }}
                        >
                          {`x ${entry.line.item.qty} ${entry.line.item.product.name}`}
                        </span>
                        <span
                          className={styles.subTotalPrice}
                          style={{
                            fontSize: cartItemPriceFontSize,
                            color: cartItemPriceFontColor,
                          }}
                        >
                          {formatAmount(getTotalPrice(entry.line.item))}
                        </span>
                      </div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              <div className={styles.orderLines}>
                <DealNotice />
                <DealNudge />
                {entries.map((entry) => {
                  if (entry.type === "deal") {
                    const { deal } = entry;
                    return (
                      <DealGroup
                        key={deal.instanceId}
                        deal={deal}
                        onEdit={
                          deals.some((x) => x.code === deal.promotionCode)
                            ? () =>
                                openDealEditor(
                                  deal.promotionCode,
                                  deal.members.map((x) => x.line),
                                )
                            : undefined
                        }
                        onRemoveMember={(line) =>
                          setRemoveDealItem({ deal, line })
                        }
                        onRemove={() =>
                          setRemoveDeal({
                            name: deal.name,
                            lineIds: deal.members.map((x) => x.line.id),
                          })
                        }
                      />
                    );
                  }
                  const { line } = entry;
                  return (
                    <CartLineView
                      key={line.id}
                      line={line}
                      updateLine={(line: CartLine) => {
                        if (line.item.qty === 0) {
                          setRemoveOrderItemId(line.id);
                        } else {
                          updateOrderLine(line);
                        }
                      }}
                      openEditLineModal={() => setSelectedLine(line)}
                      orderSource={orderSource}
                      dealHint={
                        partialDeal?.countedLineIds.includes(line.id)
                          ? dealHint
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>
          <div
            className={clsx(
              styles.orderAmounts,
              hideAmountsForMobile && styles.showInMobile,
              orderLoading && styles.repricing,
            )}
            aria-busy={orderLoading}
          >
            {orderLoading && (
              <div className={styles.repricingLabel} role="status">
                Updating prices…
              </div>
            )}
            {getAmounts(
              orderSummary,
              formatAmount,
              currency,
              surchargeInfo,
              {
                discountClass: styles.discountRow,
                chargeClass: styles.amountRow,
                totalDiscountClass: styles.savedRow,
                totalClass: styles.totalRow,
              },
              offerSavingColour(template),
            ).map(({ className, label, amount, style }, index) => (
              <div key={index} className={className} style={style}>
                <div>{label}</div>
                <div>{amount}</div>
              </div>
            ))}
            {authenticatedUser &&
              useComoAuth &&
              orderSummary.finalPriceIncTax > 0 && <LoyaltyEarnings />}
          </div>
          <div className={styles.promoCodeSection}>
            {/* The minimum is a gate, so it comes before the offers, which are a bonus */}
            <MinimumCard />
            <PromoCode />
            <UnlockedDeals />
          </div>
        </>
      ) : (
        <>
          <div className={styles.emptyCart}>
            <EmptyCartIcon />
            <span className={styles.emptyCartTitle}>Your cart is empty</span>
            <span className={styles.emptyCartDescription}>
              Start adding your favorite items to place an order
            </span>
          </div>
          <div className={styles.promoCodeSection} style={{ marginTop: 24 }}>
            <PromoCode />
            <UnlockedDeals />
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
